import React, { useEffect, useState } from 'react';
import { BookIcon, ChevronRightIcon, CreditCardIcon } from 'lucide-react';
import { PaymentsList } from './PaymentsList';
import { PaymentDetail } from './PaymentDetail';
import { formatCurrency } from '../../utils/currencyFormatter';
export type PaymentStatus = 'Cleared' | 'Pending' | 'Failed' | 'Cancelled';
export type PaymentMethod = 'Bank Transfer' | 'Cheque' | 'Credit Card' | 'BPAY' | 'Other';
export interface LinkedInvoice {
  invoiceId: string;
  invoiceNumber: string;
  vendorId: string;
  vendorName: string;
  invoiceDate: string;
  invoiceTotal: number;
  amountPaid: number;
  remainingBalance?: number;
}
export interface Payment {
  id: string;
  paymentReference: string;
  paymentDate: string;
  amount: number;
  currency: string;
  status: PaymentStatus;
  method: PaymentMethod;
  externalSourceId?: string;
  entityId: string;
  entityName: string;
  siteId?: string;
  siteName?: string;
  linkedInvoices: LinkedInvoice[];
  createdAt: string;
  sourceSystem: string;
  notes?: string;
}
export function PaymentsView({
  locale = 'en-US'
}) {
  const [view, setView] = useState<'list' | 'detail'>('list');
  const [selectedPayment, setSelectedPayment] = useState<Payment | null>(null);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [loading, setLoading] = useState(true);
  // Load sample data
  useEffect(() => {
    // In a real app, this would be an API call
    const samplePayments: Payment[] = [{
      id: 'pmt-001',
      paymentReference: 'PMT-2023-001',
      paymentDate: '2023-10-15',
      amount: 1250.75,
      currency: 'USD',
      status: 'Cleared',
      method: 'Bank Transfer',
      externalSourceId: 'ERP-PMT-12345',
      entityId: 'entity-001',
      entityName: 'Global Technologies Inc.',
      siteId: 'site-001',
      siteName: 'Headquarters',
      linkedInvoices: [{
        invoiceId: 'inv-001',
        invoiceNumber: 'INV-2023-001',
        vendorId: 'vendor-001',
        vendorName: 'Office Depot',
        invoiceDate: '2023-10-01',
        invoiceTotal: 1250.75,
        amountPaid: 1250.75,
        remainingBalance: 0
      }],
      createdAt: '2023-10-16T10:30:00Z',
      sourceSystem: 'SAP ERP'
    }, {
      id: 'pmt-002',
      paymentReference: 'PMT-2023-002',
      paymentDate: '2023-10-12',
      amount: 5430.0,
      currency: 'USD',
      status: 'Cleared',
      method: 'Bank Transfer',
      externalSourceId: 'ERP-PMT-12346',
      entityId: 'entity-001',
      entityName: 'Global Technologies Inc.',
      siteId: 'site-001',
      siteName: 'Headquarters',
      linkedInvoices: [{
        invoiceId: 'inv-002',
        invoiceNumber: 'INV-2023-002',
        vendorId: 'vendor-002',
        vendorName: 'Dell Technologies',
        invoiceDate: '2023-09-25',
        invoiceTotal: 5430.0,
        amountPaid: 3000.0,
        remainingBalance: 2430.0
      }, {
        invoiceId: 'inv-003',
        invoiceNumber: 'INV-2023-003',
        vendorId: 'vendor-002',
        vendorName: 'Dell Technologies',
        invoiceDate: '2023-09-28',
        invoiceTotal: 2430.0,
        amountPaid: 2430.0,
        remainingBalance: 0
      }],
      createdAt: '2023-10-13T09:15:00Z',
      sourceSystem: 'SAP ERP'
    }, {
      id: 'pmt-003',
      paymentReference: 'PMT-2023-003',
      paymentDate: '2023-10-18',
      amount: 12500.0,
      currency: 'USD',
      status: 'Pending',
      method: 'Cheque',
      externalSourceId: 'ERP-PMT-12347',
      entityId: 'entity-001',
      entityName: 'Global Technologies Inc.',
      siteId: 'site-001',
      siteName: 'Headquarters',
      linkedInvoices: [{
        invoiceId: 'inv-005',
        invoiceNumber: 'INV-2023-005',
        vendorId: 'vendor-005',
        vendorName: 'Server Solutions Inc.',
        invoiceDate: '2023-10-10',
        invoiceTotal: 12500.0,
        amountPaid: 12500.0,
        remainingBalance: 0
      }],
      createdAt: '2023-10-18T14:45:00Z',
      sourceSystem: 'SAP ERP'
    }, {
      id: 'pmt-004',
      paymentReference: 'PMT-2023-004',
      paymentDate: '2023-10-05',
      amount: 3200.5,
      currency: 'USD',
      status: 'Cleared',
      method: 'Credit Card',
      externalSourceId: 'ERP-PMT-12348',
      entityId: 'entity-001',
      entityName: 'Global Technologies Inc.',
      siteId: 'site-002',
      siteName: 'West Coast Office',
      linkedInvoices: [{
        invoiceId: 'inv-004',
        invoiceNumber: 'INV-2023-004',
        vendorId: 'vendor-003',
        vendorName: 'PrintWorks Inc.',
        invoiceDate: '2023-09-20',
        invoiceTotal: 3200.5,
        amountPaid: 3200.5,
        remainingBalance: 0
      }],
      createdAt: '2023-10-06T11:20:00Z',
      sourceSystem: 'SAP ERP',
      notes: 'Expedited payment for urgent marketing materials'
    }, {
      id: 'pmt-005',
      paymentReference: 'PMT-2023-005',
      paymentDate: '2023-10-20',
      amount: 4850.25,
      currency: 'EUR',
      status: 'Cleared',
      method: 'Bank Transfer',
      externalSourceId: 'ERP-PMT-12349',
      entityId: 'entity-002',
      entityName: 'European Division',
      siteId: 'site-003',
      siteName: 'Paris Office',
      linkedInvoices: [{
        invoiceId: 'inv-006',
        invoiceNumber: 'INV-2023-006',
        vendorId: 'vendor-004',
        vendorName: 'EuroSupplies',
        invoiceDate: '2023-10-05',
        invoiceTotal: 850.25,
        amountPaid: 850.25,
        remainingBalance: 0
      }, {
        invoiceId: 'inv-007',
        invoiceNumber: 'INV-2023-007',
        vendorId: 'vendor-004',
        vendorName: 'EuroSupplies',
        invoiceDate: '2023-10-08',
        invoiceTotal: 1500.0,
        amountPaid: 1500.0,
        remainingBalance: 0
      }, {
        invoiceId: 'inv-008',
        invoiceNumber: 'INV-2023-008',
        vendorId: 'vendor-004',
        vendorName: 'EuroSupplies',
        invoiceDate: '2023-10-12',
        invoiceTotal: 2500.0,
        amountPaid: 2500.0,
        remainingBalance: 0
      }],
      createdAt: '2023-10-21T08:10:00Z',
      sourceSystem: 'SAP ERP'
    }, {
      id: 'pmt-006',
      paymentReference: 'PMT-2023-006',
      paymentDate: '2023-10-22',
      amount: 12500.0,
      currency: 'USD',
      status: 'Pending',
      method: 'Bank Transfer',
      externalSourceId: 'ERP-PMT-12350',
      entityId: 'entity-001',
      entityName: 'Global Technologies Inc.',
      siteId: 'site-001',
      siteName: 'Headquarters',
      linkedInvoices: [{
        invoiceId: 'inv-009',
        invoiceNumber: 'INV-2023-009',
        vendorId: 'vendor-005',
        vendorName: 'Server Solutions Inc.',
        invoiceDate: '2023-10-10',
        invoiceTotal: 8500.0,
        amountPaid: 8500.0,
        remainingBalance: 0
      }, {
        invoiceId: 'inv-010',
        invoiceNumber: 'INV-2023-010',
        vendorId: 'vendor-005',
        vendorName: 'Server Solutions Inc.',
        invoiceDate: '2023-10-15',
        invoiceTotal: 4000.0,
        amountPaid: 4000.0,
        remainingBalance: 0
      }],
      createdAt: '2023-10-22T15:30:00Z',
      sourceSystem: 'SAP ERP'
    }, {
      id: 'pmt-007',
      paymentReference: 'PMT-2023-007',
      paymentDate: '2023-09-28',
      amount: 750.0,
      currency: 'USD',
      status: 'Cancelled',
      method: 'Cheque',
      externalSourceId: 'ERP-PMT-12351',
      entityId: 'entity-001',
      entityName: 'Global Technologies Inc.',
      siteId: 'site-002',
      siteName: 'West Coast Office',
      linkedInvoices: [{
        invoiceId: 'inv-011',
        invoiceNumber: 'INV-2023-011',
        vendorId: 'vendor-006',
        vendorName: 'Maintenance Services Co.',
        invoiceDate: '2023-09-15',
        invoiceTotal: 750.0,
        amountPaid: 0,
        remainingBalance: 750.0
      }],
      createdAt: '2023-09-29T09:45:00Z',
      sourceSystem: 'SAP ERP',
      notes: 'Payment cancelled due to service dispute'
    }, {
      id: 'pmt-008',
      paymentReference: 'PMT-2023-008',
      paymentDate: '2023-10-25',
      amount: 1500.0,
      currency: 'USD',
      status: 'Failed',
      method: 'Bank Transfer',
      externalSourceId: 'ERP-PMT-12352',
      entityId: 'entity-001',
      entityName: 'Global Technologies Inc.',
      siteId: 'site-001',
      siteName: 'Headquarters',
      linkedInvoices: [{
        invoiceId: 'inv-012',
        invoiceNumber: 'INV-2023-012',
        vendorId: 'vendor-007',
        vendorName: 'Consulting Group LLC',
        invoiceDate: '2023-10-15',
        invoiceTotal: 1500.0,
        amountPaid: 0,
        remainingBalance: 1500.0
      }],
      createdAt: '2023-10-25T14:20:00Z',
      sourceSystem: 'SAP ERP',
      notes: 'Bank transfer failed due to incorrect account details'
    }];
    setPayments(samplePayments);
    setLoading(false);
  }, []);
  const handleSelectPayment = (payment: Payment) => {
    setSelectedPayment(payment);
    setView('detail');
  };
  const handleBackToList = () => {
    setView('list');
    setSelectedPayment(null);
  };
  return <div className="space-y-6" data-id="element-8576">
      <div className="mb-4" data-id="element-8577">
        <h2 className="text-2xl font-bold text-gray-900" data-id="element-8578">Payments</h2>
        <p className="mt-1 text-sm text-gray-500" data-id="element-8579">
          View and track payments made against invoices in the system
        </p>
      </div>
      {/* Breadcrumbs */}
      <nav className="flex mb-4" aria-label="Breadcrumb" data-id="element-8580">
        <ol className="inline-flex items-center space-x-1 md:space-x-3" data-id="element-8581">
          <li className="inline-flex items-center" data-id="element-8582">
            <span className="inline-flex items-center text-sm font-medium text-indigo-600" data-id="element-8583">
              <BookIcon className="w-4 h-4 mr-2" data-id="element-8584" />
              Financial
            </span>
          </li>
          <li data-id="element-8585">
            <div className="flex items-center" data-id="element-8586">
              <ChevronRightIcon className="w-4 h-4 text-gray-400 mx-1" data-id="element-8587" />
              <button onClick={() => setView('list')} className="inline-flex items-center text-sm font-medium text-indigo-600 hover:text-indigo-800" data-id="element-8588">
                <CreditCardIcon className="w-4 h-4 mr-2" data-id="element-8589" />
                Payments
              </button>
            </div>
          </li>
          {view === 'detail' && selectedPayment && <li data-id="element-8590">
              <div className="flex items-center" data-id="element-8591">
                <ChevronRightIcon className="w-4 h-4 text-gray-400 mx-1" data-id="element-8592" />
                <span className="text-sm font-medium text-gray-500" data-id="element-8593">
                  {selectedPayment.paymentReference}
                </span>
              </div>
            </li>}
        </ol>
      </nav>
      {/* Main Content */}
      {loading ? <div className="flex justify-center items-center h-64" data-id="element-8594">
          <div className="text-center" data-id="element-8595">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500 mx-auto" data-id="element-8596"></div>
            <p className="mt-4 text-gray-600" data-id="element-8597">Loading payments data...</p>
          </div>
        </div> : view === 'list' ? <PaymentsList payments={payments} onSelectPayment={handleSelectPayment} locale={locale} data-id="element-8598" /> : selectedPayment && <PaymentDetail payment={selectedPayment} onBack={handleBackToList} locale={locale} data-id="element-8599" />}
    </div>;
}